import { useI18n } from '@ecom/i18n';
import { Badge } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

export interface GalleryEntry {
  readonly localId: string;
  readonly previewUrl: string;
  readonly fileName: string;
  readonly status: 'uploading' | 'error';
  readonly errorMessage?: string;
}

/**
 * Renders local blob-URL previews of IN-FLIGHT uploads only — a finished
 * one is removed by `ImageUploader` rather than kept here (the persisted
 * gallery, `ProductImageGallery`, takes over once the upload's own query
 * invalidation refetches it). This component owns display only;
 * `ImageUploader` owns the object URLs' lifecycle (creation on file pick,
 * revocation on success or unmount).
 */
export function ImageGallery({
  entries,
}: {
  entries: readonly GalleryEntry[];
}): ReactElement | null {
  const { t } = useI18n();
  if (entries.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {entries.map((entry) => (
        <div key={entry.localId} className="overflow-hidden rounded-lg border border-line">
          <img
            src={entry.previewUrl}
            alt={entry.fileName}
            className="aspect-square w-full object-cover"
          />
          <div className="p-2">
            <p className="truncate text-xs text-inkFaint">{entry.fileName}</p>
            {entry.status === 'uploading' ? (
              <Badge tone="neutral">{t('merchant.products.images.sending')}</Badge>
            ) : null}
            {entry.status === 'error' ? (
              <Badge tone="critical">
                {entry.errorMessage ?? t('merchant.products.images.error')}
              </Badge>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
